import { useEffect, useState } from 'react';
import { getQuota, startCheckout } from '../api';
import { useStoreFreeGb } from '../hooks/useStoreFreeGb';

/**
 * The free tier is five sends a month, enforced server-side with a 402. Before
 * this, a sender met that limit as a raw error string on the send they were
 * already halfway through, and there was no way to buy the paid tier at all —
 * /me/checkout had no caller anywhere in the app.
 *
 * Two jobs:
 *   - warn while there is still room ("2 sends left this month"), so the cap is
 *     never a surprise mid-transfer
 *   - offer the actual upgrade once it bites
 *
 * A practice-linked sender cannot buy the personal plan — the API returns 409
 * because it would double-bill a practice that can enable the add-on for
 * everyone. That answer is shown as information, not as an error.
 */

function Upgrade({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [err, setErr] = useState(null);

  async function go() {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      const r = await startCheckout();
      // Full-page redirect: Stripe Checkout is a hosted page, not an embed.
      window.location.href = r.checkout_url;
    } catch (e) {
      if (e.status === 409) {
        // Practice-linked. The API explains the add-on; repeat it verbatim
        // rather than paraphrasing a billing rule in two places.
        setNotice(e.data?.error || 'Your practice covers this — ask your admin.');
      } else {
        setErr(e.message || 'Could not start checkout');
      }
      setBusy(false);
      onDone?.();
    }
  }

  return (
    <>
      {!notice && (
        <button className="btn btn-primary" onClick={go} disabled={busy}>
          {busy ? 'Opening checkout…' : 'Unlimited sending — $19.99/year'}
        </button>
      )}
      {notice && <p style={{ margin: '8px 0 0', fontSize: 14 }}>{notice}</p>}
      {err && <p style={{ margin: '8px 0 0', fontSize: 14, color: '#b91c1c' }}>{err}</p>}
    </>
  );
}

/**
 * The one line under the 402 card: a practice on an Intake plan sends
 * unlimited, and Store's free allowance is the same for every account. The
 * GB figure comes from the pricing API (hooks/useStoreFreeGb) so this never
 * drifts from the number Store itself shows; while it loads it says "free".
 */
export function StoreUpsellLine() {
  const gb = useStoreFreeGb();
  const storeCopy = gb ? `${gb} GB of Store free` : 'free Store';
  return (
    <p className="muted" style={{ fontSize: 13, textAlign: 'center', marginTop: 8 }}>
      <a href="https://intake.dental/pricing?src=send_402">Intake practices send unlimited</a>
      {' — and every account gets '}
      <a href="https://store.intake.dental/?src=send_402">{storeCopy}</a>.
    </p>
  );
}

export default function QuotaNotice() {
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    getQuota().then((r) => setQuota(r.quota)).catch(() => setQuota(null));
  }, []);

  if (!quota) return null;
  // Unlimited for any reason (paid, practice add-on, Autopilot) — say nothing.
  if (quota.allowed && !Number.isFinite(quota.remaining)) return null;

  const exhausted = !quota.allowed;
  // Stay quiet until the cap is close enough to matter.
  if (!exhausted && quota.remaining > 2) return null;

  const resets = quota.resets_at
    ? new Date(quota.resets_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;

  // A practice-linked sender cannot buy the personal plan — the API 409s,
  // because unlimited sending rides their practice's plan. Showing them a
  // $19.99 button would be offering something we then refuse. Tell them where
  // it actually comes from instead.
  const isPractice = quota.upgrade_kind === 'practice_addon';

  if (isPractice) {
    return (
      <div className="card" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
        <h2 style={{ marginTop: 0, fontSize: 17 }}>
          {exhausted
            ? "This practice has used its five free sends this month"
            : `${quota.remaining} free ${quota.remaining === 1 ? 'send' : 'sends'} left this month`}
        </h2>
        <p className="muted" style={{ fontSize: 14, marginTop: -4, marginBottom: 0 }}>
          Unlimited sending is included with Practice Autopilot and AI Dental Office, or as a
          $14.99/year add-on that covers your whole office. Whoever manages billing can
          turn either on.
          {resets ? ` Free sends reset on ${resets}.` : ''}
        </p>
      </div>
    );
  }

  return (
    <div
      className="card"
      style={{
        background: exhausted ? '#fff7ed' : '#f8fafc',
        border: `1px solid ${exhausted ? '#fed7aa' : '#e2e8f0'}`,
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 17 }}>
        {exhausted
          ? "You've used your five free sends this month"
          : `${quota.remaining} free ${quota.remaining === 1 ? 'send' : 'sends'} left this month`}
      </h2>
      <p className="muted" style={{ fontSize: 14, marginTop: -4 }}>
        {exhausted
          ? `Your free sends reset on ${resets}. Unlimited sending is $19.99 a year — every file size, every recipient, no monthly count, and an email the moment a recipient downloads.`
          : `They reset on ${resets}. Unlimited sending is $19.99 a year if you'd rather not think about it.`}
      </p>
      <Upgrade />
    </div>
  );
}
